import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { createDeepEqualSelector } from '../utils/customFunctions';
import { getUserIdSelector } from '../user/selectors';

const DEFAULT_OBJECT = {};
const DEFAULT_ARRAY = [];

const getProjectReducerState = (state) => state?.projectReducer || DEFAULT_OBJECT;

export const getMyProjects = createSelector(getProjectReducerState, (projectsReducerState) => {
  const result = projectsReducerState?.myProjects;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

const getEntitiesProjectsState = (state) => state?.entities?.projects || DEFAULT_OBJECT;

const getProjectIdProp = (state, props) => props?.projectId;

export const getCustomFieldsIdListForProjectId = createSelector(
  [getMyProjects, getProjectIdProp],
  (myProjects, projectId) => {
    const project = (myProjects || DEFAULT_ARRAY).find(({ _id }) => _id === projectId) || DEFAULT_OBJECT;
    const customFields = project?.customFields || DEFAULT_ARRAY;
    return isEmpty(customFields) ? DEFAULT_ARRAY : customFields?.map(({ _id }) => _id);
  }
);

export const getCustomFieldsForProjectId = createSelector(
  [getMyProjects, getProjectIdProp],
  (myProjects, projectId) => {
    const project = (myProjects || DEFAULT_ARRAY).find(({ _id }) => _id === projectId) || DEFAULT_OBJECT;
    const customFields = project?.customFields || DEFAULT_ARRAY;
    return isEmpty(customFields) ? DEFAULT_ARRAY : customFields?.sort((fieldA, fieldB) => {
      if (fieldA.name < fieldB.name) {
        return -1;
      }
      if (fieldA.name > fieldB.name) {
        return 1;
      }
      return 0;
    });
  }
);

const getTaskProjectById = createSelector(
  [getEntitiesProjectsState, getProjectIdProp],
  (projectEntities, projectId) => {
    const result = projectEntities?.[projectId];
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

const getTaskProjectTitle = createSelector(getTaskProjectById, (taskProject) => taskProject?.title);

export const makeGetTaskProjectTitle = () => getTaskProjectTitle;

const getTaskProjectColour = createSelector(getTaskProjectById, (taskProject) => taskProject?.metadata?.colour);

export const makeGetTaskProjectColour = () => getTaskProjectColour;

const getTaskProjectSectionIdList = createSelector(getTaskProjectById, (taskProject) => {
  const result = taskProject?.sections;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

export const makeGetTaskProjectSectionIdList = () => getTaskProjectSectionIdList;

const getOldProjectReducer = state => state.projectReducer || DEFAULT_OBJECT;

export const getprojectCustomFields = createSelector(
  getOldProjectReducer,
  projectReducer => {
    const result = projectReducer?.projectCustomFields;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  }
);

const getCustomFieldIdProp = (state, props) => props?.customFieldId;

export const getCustomFieldNameSelector = createSelector(
  [getprojectCustomFields, getCustomFieldIdProp],
  (customFields, customFieldId) => {
    const field = customFields?.find(customField => customField?._id === customFieldId);
    return field?.name;
  }
);

export const makeGetCustomFieldNameSelector = () => getCustomFieldNameSelector;

export const getprojectCustomFieldsSelector = createDeepEqualSelector(
  getprojectCustomFields,
  customFields => customFields
);

const getCustomFieldsIdList = createSelector(
  getprojectCustomFields,
  customFields => customFields?.map(field => field?._id)
);

export const getCustomFieldsIdListSelector = createDeepEqualSelector(
  getCustomFieldsIdList,
  customFieldsIdList => customFieldsIdList
);

export const getCustomFieldsLength = createSelector(
  getCustomFieldsIdList,
  customFieldsIdList => customFieldsIdList?.length
);

const projectDataSelector = createSelector(
  getOldProjectReducer,
  projectReducer => {
    const result = projectReducer?.getProjects;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  }
);

const getProjectDataSortedSectionsSelector = createSelector(
  projectDataSelector,
  projectData => {
    const result = projectData?.sections?.sort((a, b) => a.position - b.position);
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  }
);

const getProjectDataSortedSectionIdList = createSelector(
  getProjectDataSortedSectionsSelector,
  sections => {
    const result = sections.map(section => section?._id);
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  }
);

export const getProjectDataSortedSectionIdListSelector = createDeepEqualSelector(
  getProjectDataSortedSectionIdList,
  sectionIdList => sectionIdList.map(id => id)
);

const getSectionIdProp = (state, props) => props.sectionId;

const getProjectDataSectionById = createSelector(
  [projectDataSelector, getSectionIdProp],
  (projectData, sectionId) => {
    const result = (projectData?.sections || DEFAULT_ARRAY).find(section => section?._id === sectionId);
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  }
);

const getProjectDataSectionTitle = createSelector(
  getProjectDataSectionById,
  section => section?.title
);

export const makeGetProjectDataSectionTitle = () => getProjectDataSectionTitle;

const getProjectDataSectionColor = createSelector(
  getProjectDataSectionById,
  section => section?.color
);

export const makeGetProjectDataSectionColor = () => getProjectDataSectionColor;

export const getParticipantsSelector = createSelector(
  projectDataSelector,
  projectData => {
    const result = projectData?.participants;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  }
);

export const hasParticipantsSelector = createSelector(
  getParticipantsSelector,
  participants => participants.length > 0
);

export const getParticipantsSelectOptions = createSelector(
  getParticipantsSelector,
  participants => participants?.map((user) => {
    return {
      value: user._id,
      label: user.fullName,
      email: user.email,
      avi: user.avi,
    };
  })
);

export const getProjectFromMyProjects = createSelector(
  [getMyProjects, getProjectIdProp],
  (myProjects, projectId) => {
    const project = (myProjects || DEFAULT_ARRAY)?.find(({ _id }) => _id === projectId);
    return isEmpty(project) ? DEFAULT_OBJECT : project;
  }
);

export const getMyProjectParticipants = createSelector(
  [getProjectFromMyProjects],
  (myProject) => {
    const result = myProject?.participants;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  }
);

export const hasProjectDeleteTaskRestrictionEnabled = createSelector(
  [getProjectFromMyProjects],
  (myProject) => myProject?.deleteTaskRestriction,
);

export const getCurrentUserParticipantObject = createSelector(
  [getMyProjectParticipants, getUserIdSelector],
  (participants, userId) => {
    const result = (participants || DEFAULT_ARRAY)?.find(participant => participant?._id === userId);
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  }
);

export const isCurrentUserProjectOwnerOrAdmin = createSelector(
  [getCurrentUserParticipantObject],
  (currentUserParticipant) => ['admin', 'owner']?.includes(currentUserParticipant?.role)
);

export const isCurrentUserProjectOwnerAdminMember = createSelector(
  [getCurrentUserParticipantObject],
  (currentUserParticipant) =>
    ['admin', 'member', 'owner']?.includes(currentUserParticipant?.role),
);

export const hasDeleteTaskRightsBasedOnRoleSelector = createSelector(
  [
    hasProjectDeleteTaskRestrictionEnabled,
    isCurrentUserProjectOwnerOrAdmin,
    isCurrentUserProjectOwnerAdminMember,
  ],
  (deleteTaskRestrictionEnabled, isAdminOwner, isMemberAdminOwner) =>
    deleteTaskRestrictionEnabled ? isAdminOwner : isMemberAdminOwner,
);
